import { z } from "zod"
import { TranslationKey, Translator } from "../i18n/translation-key"
import { ServiceAction, ServicePartActionUtils } from "./service-part-action"

// Part IDs for dampers (constants)
export const DAMPER_PART_IDS = {
  FRONT_FORK: "1c49ada3-5698-4517-ae86-5e6885cd60c9", // Framgaffel (framdämpare)
  REAR_SHOCK: "fae917a1-46ad-4d6b-bb2b-cc02c77ad298", // Bakdämpare
} as const

export const DROPPER_POST_PART_ID = "298c67be-a15a-426d-95c7-721040670501"

// All part IDs that belong to the "Dämparservice" filter group
export const DAMPER_SERVICE_FILTER_PART_IDS = new Set<string>([
  DAMPER_PART_IDS.FRONT_FORK,
  DAMPER_PART_IDS.REAR_SHOCK,
  DROPPER_POST_PART_ID,
])

// Actions shown under the "Dämparservice" filter
export const DAMPER_SERVICE_FILTER_ACTIONS = new Set<ServiceAction>([
  ServiceAction.BASIC_SERVICE,
  ServiceAction.FULL_SERVICE,
])

export function isDamperServiceFilterPart(partId: string): boolean {
  return DAMPER_SERVICE_FILTER_PART_IDS.has(partId)
}

export function isDamperServiceFilterAction(action: ServiceAction): boolean {
  return DAMPER_SERVICE_FILTER_ACTIONS.has(action)
}

// Rigid front fork - mutually exclusive with damped front fork
export const RIGID_FRONT_FORK_PART_ID = "49c90437-b3ca-481a-9a7c-4fac7045d6c5"

// Parts that should be excluded when a damper part is present
// Key: damper part ID, Value: array of part IDs to exclude
export const DAMPER_PART_EXCLUSIONS: Record<string, string[]> = {
  [DAMPER_PART_IDS.FRONT_FORK]: [RIGID_FRONT_FORK_PART_ID],
}

export type DamperPartId =
  (typeof DAMPER_PART_IDS)[keyof typeof DAMPER_PART_IDS]

export function isDamperPart(partId: string): partId is DamperPartId {
  return Object.values(DAMPER_PART_IDS).includes(partId as DamperPartId)
}

// Get parts that should be excluded based on present damper parts
export function getExcludedPartIds(presentPartIds: string[]): string[] {
  const excluded: string[] = []
  for (const partId of presentPartIds) {
    const exclusions = DAMPER_PART_EXCLUSIONS[partId]
    if (exclusions) {
      excluded.push(...exclusions)
    }
  }
  return excluded
}

// Default service type names for damper service
export const DAMPER_SERVICE_DEFAULT_NAME_KEYS: Partial<
  Record<ServiceAction, TranslationKey>
> = {
  [ServiceAction.BASIC_SERVICE]: "common.damperService.basicService",
  [ServiceAction.FULL_SERVICE]: "common.damperService.fullService",
  [ServiceAction.TROUBLESHOOT]: "common.damperService.troubleshoot",
}

// Actions available for damper service
export const DAMPER_SERVICE_ACTIONS: readonly ServiceAction[] = [
  ServiceAction.BASIC_SERVICE,
  ServiceAction.FULL_SERVICE,
  ServiceAction.TROUBLESHOOT,
]

export function getDamperServiceActions(): readonly ServiceAction[] {
  return DAMPER_SERVICE_ACTIONS
}

export type DamperServiceAction =
  | ServiceAction.BASIC_SERVICE
  | ServiceAction.FULL_SERVICE
  | ServiceAction.TROUBLESHOOT

// Type guard to check if an action is a damper service action
export function isDamperServiceAction(
  action: ServiceAction,
): action is DamperServiceAction {
  return getDamperServiceActions().some((a) => a === action)
}

// DamperBrand
export const DamperBrandSchema = z.object({
  id: z.string().uuid(),
  name: z.string().min(1),
  createdAt: z.coerce.date(),
})

export const CreateDamperBrandSchema = DamperBrandSchema.pick({
  name: true,
})

export type DamperBrand = z.infer<typeof DamperBrandSchema>
export type CreateDamperBrand = z.infer<typeof CreateDamperBrandSchema>

// StoreSupportedDamperBrand - junction table for which brands a store services
export const StoreSupportedDamperBrandSchema = z.object({
  storeId: z.string().uuid(),
  damperBrandId: z.string().uuid(),
})

export type StoreSupportedDamperBrand = z.infer<
  typeof StoreSupportedDamperBrandSchema
>

// DamperServicePrice
export const DamperServicePriceSchema = z.object({
  id: z.string().uuid(),
  storeId: z.string().uuid(),
  damperBrandId: z.string().uuid(),
  partId: z.string().uuid(),
  action: z.nativeEnum(ServiceAction),
  price: z.number().int().min(0),
  description: z.string(),
})

export const UpsertDamperServicePriceSchema = DamperServicePriceSchema.omit({
  id: true,
  storeId: true,
})

export type DamperServicePrice = z.infer<typeof DamperServicePriceSchema>
export type UpsertDamperServicePrice = z.infer<
  typeof UpsertDamperServicePriceSchema
>

// DamperServiceActionName
export const DamperServiceActionNameSchema = z.object({
  id: z.string().uuid(),
  storeId: z.string().uuid(),
  partId: z.string().uuid(),
  action: z.nativeEnum(ServiceAction),
  customName: z.string().min(1),
})

export const UpsertDamperServiceActionNameSchema =
  DamperServiceActionNameSchema.omit({
    id: true,
    storeId: true,
  })

export type DamperServiceActionName = z.infer<
  typeof DamperServiceActionNameSchema
>
export type UpsertDamperServiceActionName = z.infer<
  typeof UpsertDamperServiceActionNameSchema
>

// DamperBookingInfo - submitted by customer during booking
export const DamperBookingInfoSchema = z.object({
  id: z.string().uuid(),
  jobId: z.string().uuid(),
  partId: z.string().uuid(),
  damperBrandId: z.string().uuid().nullable(),
  serialNumber: z.string().nullable(),
  lastServicedAt: z.coerce.date().nullable(),
  hoursSinceService: z.number().int().min(0).nullable(),
  isDismountedOnDrop: z.boolean(),
  imageUrl: z.string().nullable(),
  selectedAction: z.nativeEnum(ServiceAction).nullable(),
  /** Custom label for the selected action (e.g., for store-specific damper service names) */
  selectedActionLabel: z.string().optional(),
  createdAt: z.coerce.date(),
})

// Schema for booking submission (from marketplace form)
export const CreateDamperBookingInfoSchema = z.object({
  partId: z.string().uuid(),
  damperBrandId: z.string().uuid().nullable(),
  serialNumber: z.string().optional().nullable(),
  lastServicedAt: z.coerce.date().optional().nullable(),
  hoursSinceService: z.number().int().min(0).optional().nullable(),
  isDismountedOnDrop: z.boolean().default(false),
  imageUrl: z.string().optional().nullable(),
  selectedAction: z.nativeEnum(ServiceAction).nullable(),
})

export type DamperBookingInfo = z.infer<typeof DamperBookingInfoSchema>
export type CreateDamperBookingInfo = z.infer<
  typeof CreateDamperBookingInfoSchema
>

// Utility functions
export function getDamperPartNameKey(partId: string): TranslationKey {
  switch (partId) {
    case DAMPER_PART_IDS.FRONT_FORK:
      return "common.damperService.part.frontFork"
    case DAMPER_PART_IDS.REAR_SHOCK:
      return "common.damperService.part.rearShock"
    default:
      return "common.damperService.part.unknown"
  }
}

export function getDamperServiceActionName(
  action: ServiceAction,
  partId: string,
  customNames: DamperServiceActionName[],
  t: Translator,
): string {
  const customName = customNames.find(
    (n) => n.partId === partId && n.action === action,
  )
  if (customName) {
    return customName.customName
  }
  return t(
    DAMPER_SERVICE_DEFAULT_NAME_KEYS[action] ??
      ServicePartActionUtils.toLabelKey(action),
  )
}

export function findDamperServicePriceEntry(
  damperBrandId: string,
  partId: string,
  action: ServiceAction,
  prices: DamperServicePrice[],
): DamperServicePrice | null {
  return (
    prices.find(
      (p) =>
        p.damperBrandId === damperBrandId &&
        p.partId === partId &&
        p.action === action,
    ) ?? null
  )
}

export function getDamperServicePrice(
  damperBrandId: string,
  partId: string,
  action: ServiceAction,
  prices: DamperServicePrice[],
): number | null {
  const entry = findDamperServicePriceEntry(
    damperBrandId,
    partId,
    action,
    prices,
  )
  return entry?.price ?? null
}

export function getDamperServiceDescription(
  damperBrandId: string,
  partId: string,
  action: ServiceAction,
  prices: DamperServicePrice[],
): string | null {
  const entry = findDamperServicePriceEntry(
    damperBrandId,
    partId,
    action,
    prices,
  )
  return entry?.description ?? null
}
